import { Button, Label, MultiSelect, SearchInput, cn } from '@atrvd/saas-ui'
import { X } from 'lucide-react'
import { CONNECTION_COPY } from '@/components/ledgersync/connectionManagement.utils'

/** The Pack and Facility multi-selects of the organization-wide page. */
export interface PackFacilityFilters {
  packOptions: Array<{ value: string; label: string }>
  packs: string[]
  onPacksChange: (value: string[]) => void
  facilityOptions: Array<{ value: string; label: string }>
  facilities: string[]
  onFacilitiesChange: (value: string[]) => void
}

interface ManualAccountFiltersProps {
  search: string
  onSearchChange: (value: string) => void
  /**
   * Pack and Facility filters; null on a facility's page, where every row
   * already belongs to the one facility and only search is offered.
   */
  packFacilityFilters: PackFacilityFilters | null
  /** Shown while any filter is set; resets them all. */
  onClear: (() => void) | null
}

/**
 * Highlights a MultiSelect whose filter is set. The component takes no
 * "active" prop, so its trigger is styled through the wrapper.
 */
const ACTIVE_FILTER = '[&_[role=combobox]]:border-blue-200'

/**
 * Toolbar of a manual bank's accounts table: search (name or number), Pack
 * and Facility multi-selects (organization-wide only), and "Clear filters". Each MultiSelect brings its
 * own clear icon; with several values selected it shows the first label and a
 * "+N" chip — saas-ui's MultiSelect offers no custom summary label.
 */
export function ManualAccountFilters({
  search,
  onSearchChange,
  packFacilityFilters,
  onClear,
}: ManualAccountFiltersProps) {
  return (
    // Labels are screen-reader only: the placeholders name each control on screen.
    <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-[220px] max-w-[340px] flex-1">
        <Label htmlFor="manual-account-search" className="sr-only">
          Search
        </Label>
        <SearchInput
          id="manual-account-search"
          placeholder={CONNECTION_COPY.searchPlaceholder}
          value={search}
          onChange={onSearchChange}
          clearable
        />
      </div>
      {packFacilityFilters && (
        <>
          <Label htmlFor="manual-account-pack-filter" className="sr-only">
            Pack
          </Label>
          <MultiSelect
            options={packFacilityFilters.packOptions}
            value={packFacilityFilters.packs}
            onChange={packFacilityFilters.onPacksChange}
            placeholder="All packs"
            className={cn('w-[160px]', packFacilityFilters.packs.length > 0 && ACTIVE_FILTER)}
            searchable
            searchPlaceholder="Search packs..."
            id="manual-account-pack-filter"
          />
          <Label htmlFor="manual-account-facility-filter" className="sr-only">
            Facility
          </Label>
          <MultiSelect
            options={packFacilityFilters.facilityOptions}
            value={packFacilityFilters.facilities}
            onChange={packFacilityFilters.onFacilitiesChange}
            placeholder="All facilities"
            className={cn('w-[200px]', packFacilityFilters.facilities.length > 0 && ACTIVE_FILTER)}
            searchable
            searchPlaceholder="Search facilities..."
            id="manual-account-facility-filter"
          />
        </>
      )}
      {onClear && (
        <Button type="button" variant="ghost" onClick={onClear}>
          <X aria-hidden="true" className="h-4 w-4" />
          Clear filters
        </Button>
      )}
    </div>
  )
}
